import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Avatar,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { green, red } from "@mui/material/colors";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import AddMember from "../management/NewMember/AddMember";

// db & storage exported from your firebase.js
import { db, storage } from "../firebase";
import { useUser } from "../UserContext";

// A small helper component for TabPanel
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ProfileCard = styled(Card)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    textAlign: "center",
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(14),
  height: theme.spacing(14),
  marginRight: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    marginRight: 0,
    marginBottom: theme.spacing(1),
  },
}));

function Profile() {
  const { user, memberData } = useUser();

  // This local state will store the currently displayed avatar URL
  // We initialize it to memberData.profilePic, if available
  const [localProfilePic, setLocalProfilePic] = useState("");
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // For toggling if the member is a Squash/Racquetball player
  const [isSquashPlayer, setIsSquashPlayer] = useState(false);
  const [isRacquetballPlayer, setIsRacquetballPlayer] = useState(false);

  // Ref to file input (so we can trigger it from a button)
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (memberData) {
      // Initialize local states from memberData
      setLocalProfilePic(memberData.profilePic || "");
      setIsSquashPlayer(!!memberData.squashPlayer);
      setIsRacquetballPlayer(!!memberData.racquetballPlayer);
    }
  }, [memberData]);

  const handleEditDialogToggle = () => {
    setEditDialogOpen(!editDialogOpen);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Toggle Squash player
  const handleToggleSquash = async (event) => {
    const newVal = event.target.checked;
    setIsSquashPlayer(newVal);

    try {
      if (memberData?.id) {
        await updateDoc(doc(db, "members", memberData.id), {
          squashPlayer: newVal,
        });
      }
    } catch (error) {
      console.error("Error updating squashPlayer:", error);
    }
  };

  // Toggle Racquetball player
  const handleToggleRacquetball = async (event) => {
    const newVal = event.target.checked;
    setIsRacquetballPlayer(newVal);

    try {
      if (memberData?.id) {
        await updateDoc(doc(db, "members", memberData.id), {
          racquetballPlayer: newVal,
        });
      }
    } catch (error) {
      console.error("Error updating racquetballPlayer:", error);
    }
  };

  // Trigger hidden file input when user clicks "Change Avatar" button
  const handleChangeAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle the file upload to Firebase Storage
  const handleAvatarFileChange = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];
    if (!file) return;

    if (!user?.uid) {
      console.warn("No auth user found or user.uid is missing, cannot upload avatar.");
      return;
    }

    try {
      // 1) Create a storage reference under 'avatars/<AUTH_UID>/<filename>'
      const fileRef = ref(storage, `avatars/${user.uid}/${file.name}`);

      // 2) Upload the file
      await uploadBytes(fileRef, file);

      // 3) Get download URL
      const downloadURL = await getDownloadURL(fileRef);

      // 4) Update Firestore document with the new avatar URL
      if (memberData?.id) {
        await updateDoc(doc(db, "members", memberData.id), {
          profilePic: downloadURL,
        });
      } else {
        console.warn("No memberData.id found, cannot update Firestore doc with avatar URL.");
      }

      // 5) Optimistically update local UI:
      setLocalProfilePic(downloadURL);

    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  if (!memberData) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h6">Loading profile...</Typography>
      </Container>
    );
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    if (phone.startsWith("+61")) {
      return phone.replace("+61", "0");
    }
    return phone;
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    const {
      addressLine1,
      addressLine2,
      locality,
      state,
      postalCode,
      country,
    } = address;

    return [
      addressLine1,
      addressLine2,
      locality,
      state,
      postalCode,
      country,
    ]
      .filter((line) => line) // Remove undefined or empty lines
      .join(", ");
  };

  const {
    firstName,
    lastName,
    email,
    phoneNumber,
    gender,
    dateOfBirth,
    birthday,
    address,
  } = memberData;

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>

      <ProfileCard>
        {/* Avatar + "Change Avatar" Button */}
        <Box sx={{ position: "relative" }}>
          {/* Use localProfilePic for immediate UI updates */}
          <ProfileAvatar
            src={localProfilePic || ""}
          />
          <Tooltip title="Change Profile Picture" placement="top">
          <IconButton
            sx={{ position: "absolute", bottom: 0, right: 0 }}
            color="primary"
            component="span"
            onClick={handleChangeAvatarClick}
          >
            <PhotoCameraIcon />
          </IconButton>
          </Tooltip>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleAvatarFileChange}
          />
        </Box>

        <CardContent sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {`${firstName} ${lastName}`}
          </Typography>
          <Typography variant="body2">Email: {email}</Typography>
          <Typography variant="body2">
            Phone: {formatPhoneNumber(phoneNumber)}
          </Typography>
          <Typography variant="body2">
            Address: {formatAddress(address)}
          </Typography>
          <Typography variant="body2">Gender: {gender || "N/A"}</Typography>
          <Typography variant="body2">
            Date of Birth: {dateOfBirth || birthday || "N/A"}
          </Typography>

          {/* Sport Types section with icons */}
          <Box sx={{ mt: 2, width: "100%", textAlign: "left" }}>
            <Typography variant="h6" gutterBottom>
              Sport Types
            </Typography>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              {isSquashPlayer ? (
                <CheckCircleIcon sx={{ color: green[500], mr: 1 }} />
              ) : (
                <CancelIcon sx={{ color: red[500], mr: 1 }} />
              )}
              <Typography>Squash</Typography>
            </Box>
            <Box display="flex" alignItems="center">
              {isRacquetballPlayer ? (
                <CheckCircleIcon sx={{ color: green[500], mr: 1 }} />
              ) : (
                <CancelIcon sx={{ color: red[500], mr: 1 }} />
              )}
              <Typography>Racquetball</Typography>
            </Box>
          </Box>
        </CardContent>

        <Button
          variant="contained"
          color="primary"
          onClick={handleEditDialogToggle}
          sx={{ alignSelf: "flex-start" }}
        >
          Edit
        </Button>
      </ProfileCard>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
          <Tab label="Sport Types" />
          
        </Tabs>
      </Box>

      {/* Sport Types Tab */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant="h6" gutterBottom>
          Sport Types you play
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select which sports you play. You can choose one or both.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isSquashPlayer}
              onChange={handleToggleSquash}
              color="success"
            />
          }
          label="Squash"
        />
        <FormControlLabel
          control={
            <Switch
              checked={isRacquetballPlayer}
              onChange={handleToggleRacquetball}
              color="success"
            />
          }
          label="Racquetball"
        />
      </TabPanel>



      

      {/* Edit Member Dialog */}
      {editDialogOpen && (
        <AddMember
          isOpen={editDialogOpen}
          toggle={handleEditDialogToggle}
          member={memberData}
          edit
        />
      )}
    </Container>
  );
}

export default Profile;
